import React from 'react';
import { ChevronRight, Clock, AlertTriangle, RotateCcw, Hash } from 'lucide-react';
import { Incarico } from '../../types';
import {
    isIncaricoConcluso,
    getOrariDifformiMinuti,
    hasOrariDifformi,
    useSogliaOrariDifformi,
    formatMinutiBreve,
} from '../../utils/incaricoUtils';

interface Props {
    inc: Incarico;
    viewMode: 'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE';
    onClick: () => void;
    formatDateTime: (dateStr: string) => string;
}

const getIncaricoTitle = (inc: Incarico): string => {
    // Incarico urgente senza pratica: il titolo è il nome libero inserito dal
    // collaboratore (soggettoNome), senza prefisso mandato.
    if ((inc as any).tipoOrigine === 'URGENTE_SENZA_MANDATO' && !inc.pratica) {
        return inc.soggettoNome || `Incarico #${inc.id}`;
    }

    const praticaName = (inc.pratica as any)?.name || '';
    const prefix = praticaName.includes(' - ') ? praticaName.split(' - ')[0] : praticaName;

    // Revocato o rifiutato: si mostra solo l'identificativo del mandato, senza
    // nome del soggetto né del cliente (il rifiutato può arrivare da una lista
    // già caricata).
    if (inc.stato === 'REVOCATO' || inc.stato === 'RIFIUTATO') {
        const mandatoId = (inc.pratica as any)?.id;
        return mandatoId ? `Mandato #${mandatoId}` : `Incarico #${inc.id}`;
    }

    const soggetto = inc.soggettoNome || (() => {
        try {
            const raw = (inc.pratica as any)?.datiAggiuntiviJson;
            const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
            if (dati.soggetti?.[0]?.nome) return dati.soggetti[0].nome;
            return dati.soggetto_nome || dati.soggetto_denominazione || '';
        } catch { return ''; }
    })();

    return soggetto ? `${prefix} - ${soggetto}` : prefix || `Incarico #${inc.id}`;
};

export const IncaricoCard: React.FC<Props> = ({ inc, viewMode, onClick, formatDateTime }) => {
    // Allerta non bloccante se lo scostamento tra orari pianificati e reali
    // supera la soglia configurata in Impostazioni.
    const sogliaOrariMin = useSogliaOrariDifformi();
    const showOrariDifformi = hasOrariDifformi(inc, sogliaOrariMin);
    const minutiDifformi = showOrariDifformi ? getOrariDifformiMinuti(inc) : null;
    const isCorso = inc.stato === 'IN_CORSO';
    // Incarico assegnato ma non ancora accettato: va evidenziato.
    const isDaAccettare = inc.stato === 'ASSEGNATO' && viewMode !== 'STORICO';
    const isAccettato = inc.stato === 'ACCETTATO';
    const isRifiutato = inc.stato === 'RIFIUTATO';
    // Revocato dal titolare: resta visibile ma con uno stile proprio che prevale
    // su tutti gli altri, essendo uno stato terminale.
    const isRevocato = inc.stato === 'REVOCATO';
    const title = getIncaricoTitle(inc);

    // Etichetta libera del mandato (es. "Milano / Esselunga") con cui lo staff
    // lo riconosce. Se vuota non si mostra.
    const rifMandato = (() => {
        const v = (inc.pratica as any)?.riferimento;
        return v != null && String(v).trim() !== '' ? String(v).trim() : '';
    })();

    // "Concluso" è riferito al singolo collaboratore: report inviato per ogni
    // giornata assegnata, anche se il mandato è ancora aperto.
    // La data di oggi è locale: con toISOString (UTC) di notte sarebbe "ieri".
    const _now = new Date();
    const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
    const hasReportForToday = (inc.relazioni || []).some(
        (r: any) => typeof r?.dataRelazione === 'string' && r.dataRelazione.slice(0, 10) === today,
    );
    const isEspletato =
        (viewMode === 'OGGI' && hasReportForToday) ||
        isIncaricoConcluso(inc);

    // Giornata conclusa ma report ancora da inviare: senza un badge dedicato
    // apparirebbe "In Servizio", perché lo stato è ancora IN_CORSO.
    const endStrCard = inc.dataFine ? inc.dataFine.split('T')[0] : '';
    // Con una sessione aperta l'incarico è "In Servizio" anche se la dataFine
    // è passata, altrimenti prevarrebbe "Report".
    const hasOpenWorkSession =
        (inc.sessioni || []).some((s: any) => s.startTime && !s.endTime) || !!inc.activeSessionId;
    const isReportDaInviare =
        viewMode === 'OGGI'
        && !!endStrCard && endStrCard < today
        && !isEspletato
        && !hasOpenWorkSession
        && (inc.sessioni || []).some((s: any) => !!s.endTime)
        && (!inc.relazioni || inc.relazioni.length === 0);

    // Relazioni da rifare (DA_MODIFICARE): segnalate già nell'elenco, senza
    // dover aprire il mandato.
    const reportDaModificare = (inc.relazioni || []).filter(
        (r: any) => r?.stato === 'DA_MODIFICARE'
    );
    const hasReportDaModificare = reportDaModificare.length > 0;

    // In storico l'incarico mai avviato è "Non avviato" invece di "Archiviato";
    // revocato e rifiutato mantengono la loro etichetta.
    const neverStarted = (inc.sessioni || []).length === 0;
    const isNonAvviato = neverStarted && inc.stato !== 'RIFIUTATO' && inc.stato !== 'REVOCATO';

    const badgeClass = isRevocato
        ? 'bg-red-100 text-red-700'
        : viewMode === 'STORICO'
        ? 'bg-slate-200 text-slate-700'
        : viewMode === 'FUTURI'
        ? 'bg-teal-100 text-teal-700'
        : isEspletato
        ? 'bg-emerald-600 text-white'
        : isReportDaInviare
        ? 'bg-orange-100 text-orange-700 animate-pulse'
        : isCorso
        ? 'bg-emerald-100 text-emerald-700 animate-pulse'
        : isDaAccettare
        ? 'bg-amber-100 text-amber-800 animate-pulse'
        : isRifiutato
        ? 'bg-rose-100 text-rose-700'
        : isAccettato
        ? 'bg-teal-100 text-teal-700'
        : 'bg-blue-100 text-blue-700';

    // Un report da rifare evidenzia la card in ambra; il revocato (grigio con
    // bordo rosso) ha comunque la precedenza.
    const cardClass = isRevocato
        ? 'border-red-300 bg-red-50/30 opacity-75'
        : hasReportDaModificare
        ? 'border-amber-400 ring-2 ring-amber-100 shadow-amber-100/50 shadow-md'
        : viewMode === 'STORICO'
        ? 'opacity-80 bg-slate-50 hover:opacity-100 border-slate-200'
        : viewMode === 'FUTURI'
        ? 'border-teal-200 hover:border-teal-400'
        : isEspletato
        ? 'border-emerald-300 bg-emerald-50/30'
        : isReportDaInviare
        ? 'border-orange-400 ring-2 ring-orange-100 shadow-orange-100/50 shadow-md'
        : isCorso
        ? 'border-emerald-500 shadow-emerald-100/50 shadow-lg'
        : isDaAccettare
        ? 'border-amber-400 shadow-amber-100/50 shadow-md'
        : isRifiutato
        ? 'border-rose-200 opacity-75'
        : 'border-slate-200';

    // Stesse etichette dei turni: "In Servizio" per l'attività in corso,
    // "Servizio Espletato" quando il report del giorno è stato inviato.
    const badgeLabel = isRevocato
        ? 'Revocato dal titolare'
        : viewMode === 'STORICO'
        ? (isNonAvviato ? 'Non avviato' : 'Archiviato')
        : isEspletato
        ? 'Servizio Espletato'
        : isReportDaInviare
        ? 'Report'
        : isCorso
        ? 'In Servizio'
        : isDaAccettare
        ? 'Da Accettare'
        : isRifiutato
        ? 'Rifiutato'
        : isAccettato
        ? 'Accettato'
        : viewMode === 'FUTURI'
        ? 'In Agenda'
        : 'Pronto';

    return (
        <div
            onClick={onClick}
            className={`group p-5 rounded-2xl bg-white border cursor-pointer hover:shadow-md transition-all active:scale-[0.98] ${cardClass}`}
        >
            <div className="flex justify-between items-start mb-3">
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider ${badgeClass}`}>
                    {badgeLabel}
                </span>
                <ChevronRight className="text-slate-300 group-hover:text-slate-500 transition-colors" size={20} />
            </div>
            <h3 className="font-semibold text-lg text-slate-900 truncate pr-4 uppercase">
                {title}
            </h3>
            {/* Riferimento del mandato in evidenza sotto il titolo. */}
            {rifMandato && (
                <div className="mt-1">
                    <span className="inline-flex items-center gap-1 max-w-full bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-md text-xs font-black">
                        <Hash size={12} className="shrink-0"/><span className="truncate">RIF. {rifMandato}</span>
                    </span>
                </div>
            )}
            <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500 font-medium">
                <Clock size={14} className="opacity-70 text-blue-500" />
                <span className="truncate">
                    {inc.dataInizio ? formatDateTime(inc.dataInizio) : 'N/D'}
                    {inc.dataFine ? ` - ${formatDateTime(inc.dataFine)}` : ' - (Aperto)'}
                </span>
            </div>
            {showOrariDifformi && (
                <div
                    className="mt-3 flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider"
                    title={`Scostamento totale ${formatMinutiBreve(minutiDifformi)} oltre la soglia di ${formatMinutiBreve(sogliaOrariMin)}`}
                >
                    <AlertTriangle size={13} className="shrink-0"/>
                    <span className="truncate">Orari difformi · +{formatMinutiBreve(minutiDifformi)}</span>
                </div>
            )}
            {/* Momento della revoca, sotto l'orario pianificato. */}
            {isRevocato && inc.dataRevoca && (
                <div className="mt-2 text-[10px] font-bold text-red-700/80 uppercase tracking-wider">
                    Revocato il {new Date(inc.dataRevoca).toLocaleString('it-IT', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    })}
                </div>
            )}
            {/* Report da rifare: visibile anche su incarico revocato, perché il
                collaboratore può ancora correggere il testo. */}
            {hasReportDaModificare && (
                <div
                    className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider"
                    title="Apri l'incarico, vai sul report del giorno e re-invialo dopo le correzioni"
                >
                    <RotateCcw size={13} className="shrink-0"/>
                    <span className="truncate">
                        {reportDaModificare.length === 1
                            ? '1 report da rifare'
                            : `${reportDaModificare.length} report da rifare`}
                    </span>
                </div>
            )}
        </div>
    );
};
